<template>
  <div class="system-settings" v-loading="loading">
    <section class="page-header">
      <div>
        <h1>系统设置</h1>
        <p>这里只保留通知通道设置和数据源只读说明；密钥与高级参数由 GPT 管理员通过后台维护。</p>
      </div>
    </section>

    <el-tabs v-model="activeTab" type="border-card" class="settings-tabs">
      <el-tab-pane label="通知设置" name="notification">
        <el-alert
          title="通知规则已统一归入任务：任务范围和运行时间在“任务监控”管理，发送结果在“运行记录”查看。"
          type="info"
          :closable="false"
          show-icon
          class="section-alert"
        />

        <el-row :gutter="16">
          <el-col :xs="24" :lg="14">
            <el-card shadow="never" class="setting-card">
              <div slot="header" class="card-header">
                <div>
                  <strong>发送通道</strong>
                  <p>设置消息从哪里发出。修改后只影响后续通知。</p>
                </div>
              </div>

              <el-form :model="notificationConfig" label-width="130px" size="small">
                <el-form-item label="飞书通知">
                  <el-switch v-model="notificationConfig.wechat_enabled" />
                  <span class="field-note">系统当前主通知通道</span>
                </el-form-item>
                <el-form-item label="邮件通知">
                  <el-switch v-model="notificationConfig.email_enabled" />
                  <span class="field-note">作为备用通道</span>
                </el-form-item>
                <el-form-item label="默认通道">
                  <el-radio-group v-model="notificationConfig.default_channel">
                    <el-radio label="wechat">飞书</el-radio>
                    <el-radio label="email">邮件</el-radio>
                  </el-radio-group>
                </el-form-item>
                <el-form-item label="失败重试">
                  <el-input-number v-model="notificationConfig.retry_times" :min="0" :max="10" />
                  <span class="field-note">次</span>
                </el-form-item>
                <el-form-item label="重试间隔">
                  <el-input-number v-model="notificationConfig.retry_interval" :min="10" :max="600" />
                  <span class="field-note">秒</span>
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" :loading="savingNotification" @click="saveNotificationConfig">保存通知设置</el-button>
                </el-form-item>
              </el-form>
            </el-card>
          </el-col>

          <el-col :xs="24" :lg="10">
            <el-card shadow="never" class="setting-card rule-card">
              <div slot="header" class="card-header">
                <div>
                  <strong>当前通知规则</strong>
                  <p>采用统一父任务汇总，避免同一轮任务重复推送。</p>
                </div>
              </div>

              <div class="rule-list">
                <div class="rule-line">
                  <i class="el-icon-circle-check"></i>
                  <div><strong>父任务一次汇总</strong><span>子模块只留运行记录，不单独发送。</span></div>
                </div>
                <div class="rule-line">
                  <i class="el-icon-circle-check"></i>
                  <div><strong>第一本账自动对齐</strong><span>个股监控范围跟随第一本账，不再维护第二份名单。</span></div>
                </div>
                <div class="rule-line">
                  <i class="el-icon-circle-check"></i>
                  <div><strong>失败清晰留痕</strong><span>发送成功与失败统一显示在运行记录。</span></div>
                </div>
              </div>

              <div class="shortcut-actions">
                <el-button size="small" type="primary" plain @click="goTaskMonitor">管理任务</el-button>
                <el-button size="small" @click="goNotificationRecords">查看发送记录</el-button>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <el-card shadow="never" class="setting-card test-card">
          <div slot="header" class="card-header">
            <div>
              <strong>通道测试</strong>
              <p>只在修改通道配置后使用，日常不需要重复测试。</p>
            </div>
          </div>

          <el-tabs v-model="testTab">
            <el-tab-pane label="飞书" name="feishu">
              <el-input
                v-model="feishuTest.message"
                type="textarea"
                :rows="3"
                maxlength="300"
                show-word-limit
                placeholder="输入测试消息"
              />
              <el-button
                type="success"
                size="small"
                class="test-button"
                :loading="feishuTesting"
                @click="testFeishu"
              >发送飞书测试</el-button>
              <el-alert
                v-if="feishuTestResult"
                :title="feishuTestResult"
                :type="isSuccessText(feishuTestResult) ? 'success' : 'error'"
                :closable="false"
                class="test-result"
              />
            </el-tab-pane>

            <el-tab-pane label="邮件" name="email">
              <el-input v-model.trim="emailTest.to" size="small" placeholder="收件人邮箱" class="email-field" />
              <el-input v-model.trim="emailTest.subject" size="small" placeholder="邮件主题" class="email-field" />
              <el-input v-model="emailTest.content" type="textarea" :rows="3" placeholder="邮件内容" />
              <el-button
                type="primary"
                size="small"
                class="test-button"
                :loading="emailTesting"
                @click="testEmail"
              >发送邮件测试</el-button>
              <el-alert
                v-if="emailTestResult"
                :title="emailTestResult"
                :type="isSuccessText(emailTestResult) ? 'success' : 'error'"
                :closable="false"
                class="test-result"
              />
            </el-tab-pane>
          </el-tabs>
        </el-card>
      </el-tab-pane>

      <el-tab-pane label="数据源状态" name="datasource">
        <el-alert
          title="普通页面不会主动访问数据源。这里只展示安全边界；需要实时连通性诊断或轮换密钥时，请在 GPT 聊天中发起管理请求。"
          type="info"
          :closable="false"
          show-icon
          class="section-alert"
        />

        <section class="datasource-status-grid">
          <el-card shadow="never" class="status-card">
            <div class="status-icon green"><i class="el-icon-s-operation"></i></div>
            <span>数据源管理</span>
            <strong>后台托管</strong>
            <p>网页不会因打开或刷新而调用行情数据源</p>
          </el-card>

          <el-card shadow="never" class="status-card">
            <div class="status-icon blue"><i class="el-icon-key"></i></div>
            <span>Tushare 密钥</span>
            <strong>后台安全维护</strong>
            <p>完整密钥不会返回或写入普通前端</p>
          </el-card>

          <el-card shadow="never" class="status-card">
            <div class="status-icon gray"><i class="el-icon-view"></i></div>
            <span>实时连通性</span>
            <strong>前端不主动探测</strong>
            <p>避免普通页面产生额外 Provider 调用</p>
          </el-card>
        </section>

        <el-row :gutter="16">
          <el-col :xs="24" :lg="14">
            <el-card shadow="never" class="setting-card">
              <div slot="header" class="card-header split-header">
                <div>
                  <strong>数据源安全边界</strong>
                  <p>此处不把未知或未探测状态伪装成“正常”。</p>
                </div>
              </div>

              <el-descriptions :column="1" border size="small">
                <el-descriptions-item label="密钥状态">由后台受控维护</el-descriptions-item>
                <el-descriptions-item label="到期信息">前端不提供</el-descriptions-item>
                <el-descriptions-item label="权限级别">前端不推断</el-descriptions-item>
                <el-descriptions-item label="积分信息">前端不推断</el-descriptions-item>
                <el-descriptions-item label="状态说明">实时 Provider 检查只在后台受控诊断时执行，不随页面打开或刷新触发。</el-descriptions-item>
              </el-descriptions>

              <div class="datasource-actions">
                <el-button type="primary" plain @click="goHistoryMonitor">查看数据健康</el-button>
              </div>
            </el-card>
          </el-col>

          <el-col :xs="24" :lg="10">
            <el-card shadow="never" class="setting-card admin-card">
              <div slot="header" class="card-header">
                <div>
                  <strong>密钥轮换方式</strong>
                  <p>用户只发起需求，GPT 管理员负责安全检查和后台操作。</p>
                </div>
              </div>

              <div class="rule-list">
                <div class="rule-line">
                  <i class="el-icon-chat-dot-round"></i>
                  <div><strong>在 GPT 聊天中发起</strong><span>只需说“更换 Tushare 密钥”，无需进入网页配置。</span></div>
                </div>
                <div class="rule-line">
                  <i class="el-icon-lock"></i>
                  <div><strong>不要发送完整密钥</strong><span>普通聊天消息、截图和前端输入框都不应承载完整密钥。</span></div>
                </div>
                <div class="rule-line">
                  <i class="el-icon-circle-check"></i>
                  <div><strong>安全入口通过后再切换</strong><span>GPT 会先确认可用的安全运维入口；没有安全入口时会停止，不会冒险写入。</span></div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </el-tab-pane>

      <el-tab-pane label="系统信息" name="info">
        <el-card shadow="never" class="setting-card narrow-card">
          <div slot="header" class="card-header">
            <div>
              <strong>系统信息</strong>
              <p>当前前端定位为用户运行监控台。</p>
            </div>
          </div>

          <el-descriptions :column="1" border size="small">
            <el-descriptions-item label="系统名称">小七运行监控台</el-descriptions-item>
            <el-descriptions-item label="界面模式">监控轻量版</el-descriptions-item>
            <el-descriptions-item label="通知方式">父任务汇总发送</el-descriptions-item>
            <el-descriptions-item label="前端边界">查看状态和少量日常设置，不承接股票分析决策</el-descriptions-item>
            <el-descriptions-item label="密钥维护">通过 GPT 聊天发起，用户前端不接收完整密钥</el-descriptions-item>
            <el-descriptions-item label="高级运维">由 GPT 管理员使用后台受控工具处理</el-descriptions-item>
          </el-descriptions>
        </el-card>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { notificationApi, systemSettingsApi } from '@/api'

export default {
  name: 'SystemSettingsLightweight',
  data() {
    return {
      loading: false,
      activeTab: this.normalizeTab(this.$route.query.tab),
      savingNotification: false,
      notificationConfig: {
        wechat_enabled: true,
        email_enabled: false,
        default_channel: 'wechat',
        retry_times: 3,
        retry_interval: 60
      },
      testTab: 'feishu',
      feishuTest: {
        message: '小七通知通道测试：飞书发送正常。'
      },
      feishuTesting: false,
      feishuTestResult: '',
      emailTest: {
        to: '',
        subject: '小七通知测试',
        content: '这是一封通知通道测试邮件。'
      },
      emailTesting: false,
      emailTestResult: ''
    }
  },
  watch: {
    '$route.query.tab'(value) {
      this.activeTab = this.normalizeTab(value)
    }
  },
  mounted() {
    this.refreshData()
  },
  methods: {
    normalizeTab(value) {
      return ['notification', 'datasource', 'info'].includes(value) ? value : 'notification'
    },
    unwrapResponse(response) {
      return (response && (response.machine_payload || response.data || response.payload)) || response || {}
    },
    async refreshData() {
      this.loading = true
      try {
        await this.loadConfig()
      } finally {
        this.loading = false
      }
    },
    async loadConfig() {
      try {
        const response = await systemSettingsApi.getConfig()
        const payload = this.unwrapResponse(response)
        const config = payload.config && typeof payload.config === 'object' ? payload.config : payload
        if (config.notification && typeof config.notification === 'object') {
          this.notificationConfig = { ...this.notificationConfig, ...config.notification }
        }
      } catch (error) {
        this.$message.warning('通知配置暂时不可用，页面已保留安全默认值。')
      }
    },
    async saveNotificationConfig() {
      this.savingNotification = true
      try {
        await systemSettingsApi.updateNotificationConfig(this.notificationConfig)
        this.$message.success('通知设置已保存')
      } catch (error) {
        this.$message.error('通知设置保存失败')
      } finally {
        this.savingNotification = false
      }
    },
    async testFeishu() {
      if (!String(this.feishuTest.message || '').trim()) {
        this.$message.warning('请输入测试消息')
        return
      }
      this.feishuTesting = true
      this.feishuTestResult = ''
      try {
        const response = await notificationApi.testTemplate({ message: this.feishuTest.message })
        const payload = this.unwrapResponse(response)
        this.feishuTestResult = payload.visible_stub || payload.message || (payload.success === false ? '发送失败' : '发送成功')
      } catch (error) {
        this.feishuTestResult = '发送失败'
      } finally {
        this.feishuTesting = false
      }
    },
    async testEmail() {
      if (!this.emailTest.to) {
        this.$message.warning('请输入收件人邮箱')
        return
      }
      this.emailTesting = true
      this.emailTestResult = ''
      try {
        const response = await notificationApi.testEmail(this.emailTest)
        const payload = this.unwrapResponse(response)
        this.emailTestResult = payload.visible_stub || payload.message || (payload.success === false ? '发送失败' : '发送成功')
      } catch (error) {
        this.emailTestResult = '发送失败'
      } finally {
        this.emailTesting = false
      }
    },
    isSuccessText(text) {
      const value = String(text || '')
      return value.includes('成功') || value.includes('已发送') || value.includes('已送达')
    },
    goTaskMonitor() {
      this.$router.push('/scheduled-tasks')
    },
    goNotificationRecords() {
      this.$router.push({ path: '/scheduled-task-runs', query: { section: 'notifications' } })
    },
    goHistoryMonitor() {
      this.$router.push('/history-monitor')
    }
  }
}
</script>

<style scoped>
.system-settings { max-width: 1240px; margin: 0 auto; }
.page-header { min-height: 72px; padding: 8px 2px 14px; border-bottom: 1px solid #e8eef5; display: flex; justify-content: space-between; align-items: center; gap: 20px; }
.page-header h1 { margin: 0 0 7px; color: #0f172a; font-size: 27px; }
.page-header p { margin: 0; color: #64748b; }
.settings-tabs { margin-top: 16px; border-radius: 14px; overflow: hidden; }
.section-alert { margin-bottom: 16px; }
.setting-card { margin-bottom: 16px; border-radius: 14px; }
.card-header strong { color: #0f172a; }
.card-header p { margin: 4px 0 0; color: #7f8ea3; font-size: 12px; }
.split-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.field-note { margin-left: 10px; color: #94a3b8; font-size: 12px; }
.rule-card { min-height: 365px; }
.rule-list { display: flex; flex-direction: column; gap: 16px; }
.rule-line { display: grid; grid-template-columns: 26px 1fr; gap: 10px; align-items: flex-start; }
.rule-line i { color: #16a34a; font-size: 20px; }
.rule-line strong, .rule-line span { display: block; }
.rule-line strong { color: #334155; }
.rule-line span { margin-top: 4px; color: #7f8ea3; font-size: 12px; line-height: 1.6; }
.shortcut-actions { display: flex; gap: 10px; margin-top: 22px; }
.test-card { max-width: 900px; }
.test-button { margin-top: 10px; }
.test-result { margin-top: 10px; }
.email-field { margin-bottom: 8px; }
.narrow-card { max-width: 820px; }
.datasource-status-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
.status-card { border-radius: 14px; }
.status-card /deep/ .el-card__body { min-height: 126px; display: grid; grid-template-columns: 48px 1fr; grid-template-rows: auto auto auto; column-gap: 14px; align-items: center; padding: 18px; }
.status-icon { grid-row: 1 / span 3; width: 46px; height: 46px; border-radius: 14px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 22px; background: #94a3b8; }
.status-icon.green { background: #16a34a; }
.status-icon.red { background: #ef4444; }
.status-icon.orange { background: #f59e0b; }
.status-icon.blue { background: #2563eb; }
.status-icon.gray { background: #94a3b8; }
.status-card span { color: #64748b; font-size: 12px; }
.status-card strong { color: #0f172a; font-size: 19px; }
.status-card p { margin: 0; color: #94a3b8; font-size: 12px; }
.datasource-actions { margin-top: 16px; }
.admin-card { min-height: 335px; }
.admin-card .rule-line i { color: #2563eb; }
@media (max-width: 900px) { .datasource-status-grid { grid-template-columns: 1fr; } }
@media (max-width: 760px) {
  .page-header, .split-header { align-items: flex-start; flex-direction: column; }
  .shortcut-actions { flex-direction: column; }
}
</style>